import { createContext, useCallback, useContext, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getNote } from "@api";
import { useUserName } from "@hooks";
import NoteDetailModal from "@components/NoteDetailModal";

const NoteDrawerContext = createContext(null);

export const useNoteDrawer = () =>
  useContext(NoteDrawerContext) ?? { openNote: () => {} };

// Mounts the note detail modal once, app-wide, so a note-tag notification can open the note over
// the current page. Fetches the note by id and shapes it like an activity-feed row so it reuses the
// same NoteDetailModal the AI activity feed uses. Elevated so it sits above the settings modal.
export function NoteDrawerProvider({ children }) {
  const [noteId, setNoteId] = useState(null);
  const openNote = useCallback((id) => setNoteId(id), []);

  const { data: note } = useQuery({
    queryKey: ["note", noteId],
    queryFn: () => getNote(noteId),
    enabled: !!noteId,
  });

  // Ontik-authored notes carry the author on CreatedByUserGuid (resolved client-side); ERP notes
  // carry AuthorName directly.
  const resolvedAuthor = useUserName(note?.createdByUserGuid);

  const activity = note
    ? {
        body: note.content,
        occurredAt: note.createdOn,
        actorName: note.authorName || resolvedAuthor || null,
        taggedUsers: note.taggedUsers ?? [],
      }
    : null;

  return (
    <NoteDrawerContext.Provider value={{ openNote }}>
      {children}
      <NoteDetailModal
        activity={activity}
        isOpen={!!noteId}
        onClose={() => setNoteId(null)}
        elevated
      />
    </NoteDrawerContext.Provider>
  );
}
